import { useCallback, useState } from 'react';
import {
  startInterview,
  getInterview,
  getNextQuestion,
  submitResponse,
  completeInterview,
  getInterviewResult,
} from '../lib/interviewApi';

/**
 * State machine for an interview session.
 *
 * Phases:
 *   idle       — nothing loaded yet
 *   loading    — waiting for backend response
 *   intro      — session started, showing intro screen before first question
 *   question   — displaying a question, waiting for candidate's answer
 *   submitting — sending answer to backend, waiting for evaluation
 *   completing — finalizing (LLM is generating the scoring report)
 *   complete   — done, showing result
 *   error      — something failed; error message in state
 */
export function useInterviewSession() {
  const [phase, setPhase] = useState('idle');
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const start = useCallback(async (applicationId) => {
    setPhase('loading');
    setError(null);
    try {
      const sess = await startInterview(applicationId);
      setSession(sess);
      setPhase('intro');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  }, []);

  // Resume a session that was already created by Frappe (via /from-frappe webhook).
  // Used when the candidate opens a share link of the form /interview/by-id/:interviewId.
  const resume = useCallback(async (interviewId) => {
    setPhase('loading');
    setError(null);
    try {
      const sess = await getInterview(interviewId);
      setSession(sess);
      // If candidate has already started answering, jump straight to the next
      // unanswered question. Otherwise show the intro screen.
      const responded = (sess.responses || []).length;
      const total = (sess.questions || []).length;
      if (sess.status === 'completed') {
        // Already done — fetch cached result; never re-POST /complete.
        const res = await getInterviewResult(interviewId).catch(() => null);
        setResult(res);
        setPhase('complete');
      } else if (responded > 0 && responded < total) {
        const q = await getNextQuestion(interviewId);
        if (q) {
          setCurrentQuestion(q);
          setPhase('question');
        } else {
          setPhase('intro');
        }
      } else {
        setPhase('intro');
      }
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  }, []);

  const loadNextQuestion = useCallback(async () => {
    if (!session) return;
    setPhase('loading');
    try {
      const q = await getNextQuestion(session.interview_id);
      if (!q) {
        // No more questions — move to completing (LLM scores the whole thing)
        setPhase('completing');
        const res = await completeInterview(session.interview_id);
        setResult(res);
        setPhase('complete');
      } else {
        setCurrentQuestion(q);
        setPhase('question');
      }
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  }, [session]);

  const submitAnswer = useCallback(
    async (answerText) => {
      if (!session) return;
      setPhase('submitting');
      try {
        const updated = await submitResponse(session.interview_id, answerText);
        setSession(updated);
        await loadNextQuestion();
      } catch (e) {
        setError(e.message);
        setPhase('error');
      }
    },
    [session, loadNextQuestion],
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setSession(null);
    setCurrentQuestion(null);
    setResult(null);
    setError(null);
  }, []);

  return {
    phase,
    session,
    currentQuestion,
    result,
    error,
    start,
    resume,
    loadNextQuestion,
    submitAnswer,
    reset,
  };
}
